import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db, auth } from '../services/firebase';
import { collection, getDocs, query, where, doc, getDoc, setDoc } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, sendPasswordResetEmail } from 'firebase/auth';

function Landing() {
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [user] = useAuthState(auth);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'signup'
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authName, setAuthName] = useState('');
  const [authError, setAuthError] = useState('');
  const [authSuccess, setAuthSuccess] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [userMessage, setUserMessage] = useState('');
  const [checkingUser, setCheckingUser] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Load clubs from Firestore
  useEffect(() => {
    const fetchClubs = async () => {
      try {
        const q = query(collection(db, 'clubs'), where('status', '==', 'active'));
        const snapshot = await getDocs(q);
        const clubsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        // Sort to put proseriesfc first
        clubsData.sort((a, b) => {
          if (a.subdomain === 'proseriesfc') return -1;
          if (b.subdomain === 'proseriesfc') return 1;
          return a.name.localeCompare(b.name);
        });
        setClubs(clubsData);
      } catch (error) {
        console.error('Error fetching clubs:', error);
      }
      setLoading(false);
    };
    fetchClubs();
  }, []);

  // State for user's club info
  const [userClubInfo, setUserClubInfo] = useState(null);
  const [userRole, setUserRole] = useState(null);

  // Check user's club assignment when logged in (but don't auto-redirect)
  useEffect(() => {
    const checkUserClub = async () => {
      if (!user) {
        setUserMessage('');
        setUserClubInfo(null);
        setUserRole(null);
        return;
      }
      
      setCheckingUser(true);
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserRole(userData.role);
          
          const isSuperAdmin = userData.role === 'super_admin';
          
          // User has a club assigned - get club info
          if (userData.clubId) {
            console.log('Landing: User clubId:', userData.clubId);
            const clubDoc = await getDoc(doc(db, 'clubs', userData.clubId));
            if (clubDoc.exists()) {
              const clubData = clubDoc.data();
              console.log('Landing: Club data:', clubData);
              // Use document ID as subdomain if subdomain field is missing
              const subdomain = clubData.subdomain || clubDoc.id;
              console.log('Landing: Using subdomain:', subdomain);
              setUserClubInfo({
                isSuperAdmin: isSuperAdmin,
                name: clubData.name,
                subdomain: subdomain,
                role: userData.role
              });
              setUserMessage('');
              setCheckingUser(false);
              return;
            } else {
              console.log('Landing: Club document not found for:', userData.clubId);
            }
          }
          
          // Super admin without club assigned
          if (isSuperAdmin) {
            setUserClubInfo({ isSuperAdmin: true });
            setUserMessage('');
            setCheckingUser(false);
            return;
          }
          
          // User exists but no club assigned
          setUserMessage('You are not assigned to a club yet. Contact your club administrator to be added.');
        } else {
          // New user - no user document yet
          setUserMessage('Welcome! You are not assigned to a club yet. Contact your club administrator or register a new club.');
        }
      } catch (error) {
        console.error('Error checking user:', error);
        setUserMessage('Error loading your profile. Please try again.');
      }
      setCheckingUser(false);
    };

    checkUserClub();
  }, [user]);

  // Filter clubs based on search
  const filteredClubs = clubs.filter(club =>
    club.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const features = [
    { icon: '👥', title: 'Player Management', desc: 'Track player profiles, stats, and development' },
    { icon: '📅', title: 'Schedule & Events', desc: 'Manage training sessions, matches, and tournaments' },
    { icon: '🛒', title: 'Kit Shop', desc: 'Sell merchandise with integrated payments' },
    { icon: '📋', title: 'Registration', desc: 'Online player registration with approval workflow' },
    { icon: '📊', title: 'Attendance', desc: 'Track attendance and player participation' },
    { icon: '📸', title: 'Photo Gallery', desc: 'Share memories with team photo albums' },
  ];

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthLoading(true);

    try {
      if (authMode === 'login') {
        await signInWithEmailAndPassword(auth, authEmail, authPassword);
      } else {
        // Create account and save user profile with name
        const userCredential = await createUserWithEmailAndPassword(auth, authEmail, authPassword);
        
        // Save user document with name (no club assigned yet)
        await setDoc(doc(db, 'users', userCredential.user.uid), {
          email: authEmail,
          name: authName || authEmail.split('@')[0],
          role: 'player', // Default role for new signups
          clubId: null, // Not assigned to a club yet
          createdAt: new Date()
        });
      }
      setShowAuthModal(false);
      setAuthEmail('');
      setAuthPassword('');
      setAuthName('');
    } catch (error) {
      if (error.code === 'auth/user-not-found') {
        setAuthError('No account found with this email.');
      } else if (error.code === 'auth/wrong-password') {
        setAuthError('Incorrect password.');
      } else if (error.code === 'auth/email-already-in-use') {
        setAuthError('An account with this email already exists.');
      } else if (error.code === 'auth/weak-password') {
        setAuthError('Password must be at least 6 characters.');
      } else {
        setAuthError(error.message);
      }
    }
    setAuthLoading(false);
  };

  const handleLogout = async () => {
    await signOut(auth);
    setUserMessage('');
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!authEmail) {
      setAuthError('Please enter your email address first');
      return;
    }
    setAuthError('');
    setAuthLoading(true);

    try {
      await sendPasswordResetEmail(auth, authEmail);
      setAuthSuccess('Password reset email sent! Check your inbox.');
      setShowResetPassword(false);
    } catch (error) {
      if (error.code === 'auth/user-not-found') {
        setAuthError('No account found with this email');
      } else {
        setAuthError('Failed to send reset email. Please try again.');
      }
    }
    setAuthLoading(false);
  };

  return (
    <div className="landing-page">
      {/* Header/Nav */}
      <header className="landing-header">
        <div className="landing-nav">
          <div className="nav-brand">ClubLink TT</div>
          
          {/* Hamburger Menu Button */}
          <button 
            className={`hamburger landing-hamburger ${mobileMenuOpen ? 'active' : ''}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
          </button>

          {/* Desktop Nav Links */}
          <div className="nav-links desktop-only">
            {user ? (
              <div className="user-menu">
                <span className="user-email">{user.email}</span>
                {userClubInfo?.isSuperAdmin && (
                  <Link to="/super-admin" className="btn-nav btn-nav-primary">
                    Super Admin
                  </Link>
                )}
                {userClubInfo?.subdomain && (
                  <a href={`/?club=${userClubInfo.subdomain}`} className="btn-nav btn-nav-primary">
                    {userClubInfo.role === 'club_admin' ? 'My Club Admin' : 'My Club'}
                  </a>
                )}
                <button onClick={handleLogout} className="btn-nav">Logout</button>
              </div>
            ) : (
              <>
                <button onClick={() => { setAuthMode('login'); setShowAuthModal(true); }} className="btn-nav">
                  Login
                </button>
                <button onClick={() => { setAuthMode('signup'); setShowAuthModal(true); }} className="btn-nav btn-nav-primary">
                  Sign Up
                </button>
              </>
            )}
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="landing-mobile-menu">
            {user ? (
              <>
                <span className="mobile-user-email">{user.email}</span>
                {userClubInfo?.isSuperAdmin && (
                  <Link to="/super-admin" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
                    Super Admin
                  </Link>
                )}
                {userClubInfo?.subdomain && (
                  <a href={`/?club=${userClubInfo.subdomain}`} className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
                    {userClubInfo.role === 'club_admin' ? 'My Club Admin' : 'My Club'}
                  </a>
                )}
                <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="mobile-logout-btn">
                  Logout
                </button>
              </>
            ) : (
              <>
                <button onClick={() => { setAuthMode('login'); setShowAuthModal(true); setMobileMenuOpen(false); }} className="mobile-nav-link">
                  Login
                </button>
                <button onClick={() => { setAuthMode('signup'); setShowAuthModal(true); setMobileMenuOpen(false); }} className="mobile-signup-btn">
                  Sign Up
                </button>
              </>
            )}
          </div>
        )}
      </header>
      
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && <div className="landing-mobile-overlay" onClick={() => setMobileMenuOpen(false)}></div>}

      {/* Super Admin Banner - shows for super admins (with or without club) */}
      {user && userClubInfo?.isSuperAdmin && !checkingUser && (
        <div className="user-club-banner super-admin-banner">
          <p>
            <strong>Super Admin</strong>
            {userClubInfo.subdomain && ` | ${userClubInfo.name}`}
          </p>
          <Link to="/super-admin" className="btn-small">
            Platform Admin
          </Link>
          {userClubInfo.subdomain && (
            <>
              <a href={`/?club=${userClubInfo.subdomain}`} className="btn-small">
                View Club
              </a>
              <a href={`/admin/dashboard?club=${userClubInfo.subdomain}`} className="btn-small btn-admin">
                Club Admin
              </a>
            </>
          )}
        </div>
      )}

      {/* Regular User Club Banner - shows link to their club (non-super-admin) */}
      {user && userClubInfo && !userClubInfo.isSuperAdmin && userClubInfo.subdomain && !checkingUser && (
        <div className="user-club-banner">
          <p>You are a member of <strong>{userClubInfo.name}</strong></p>
          <a href={`/?club=${userClubInfo.subdomain}`} className="btn-small">
            Go to {userClubInfo.name}
          </a>
          {(userClubInfo.role === 'club_admin' || userClubInfo.role === 'coach') && (
            <a href={`/admin/dashboard?club=${userClubInfo.subdomain}`} className="btn-small btn-admin">
              Admin Dashboard
            </a>
          )}
        </div>
      )}

      {/* User Message Banner - for users without a club */}
      {user && userMessage && !checkingUser && (
        <div className="user-message-banner">
          <p>{userMessage}</p>
          <Link to="/create-club" className="btn-small">Register a New Club</Link>
        </div>
      )}

      {/* Checking User Spinner */}
      {checkingUser && (
        <div className="checking-user-overlay">
          <div className="loading-spinner"></div>
          <p>Checking your account...</p>
        </div>
      )}

      {/* Auth Modal */}
      {showAuthModal && (
        <div className="auth-modal-overlay" onClick={() => setShowAuthModal(false)}>
          <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => { setShowAuthModal(false); setShowResetPassword(false); setAuthError(''); setAuthSuccess(''); }}>&times;</button>
            <h2>{showResetPassword ? 'Reset Password' : (authMode === 'login' ? 'Login' : 'Create Account')}</h2>
            
            {showResetPassword ? (
              <form onSubmit={handlePasswordReset}>
                <p style={{ color: '#9ca3af', marginBottom: '20px' }}>
                  Enter your email address and we'll send you a link to reset your password.
                </p>
                
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={authEmail}
                    onChange={(e) => setAuthEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                  />
                </div>

                {authError && <p className="auth-error">{authError}</p>}
                {authSuccess && <p className="auth-success">{authSuccess}</p>}

                <button type="submit" className="btn-auth" disabled={authLoading}>
                  {authLoading ? 'Sending...' : 'Send Reset Email'}
                </button>
                
                <p className="auth-switch">
                  <button onClick={() => { setShowResetPassword(false); setAuthError(''); setAuthSuccess(''); }}>
                    Back to Login
                  </button>
                </p>
              </form>
            ) : (
              <>
                <form onSubmit={handleAuth}>
                  {authMode === 'signup' && (
                    <div className="form-group">
                      <label>Full Name</label>
                      <input
                        type="text"
                        value={authName}
                        onChange={(e) => setAuthName(e.target.value)}
                        placeholder="Enter your name"
                        required={authMode === 'signup'}
                      />
                    </div>
                  )}
                  
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={authEmail}
                      onChange={(e) => setAuthEmail(e.target.value)}
                      placeholder="Enter your email"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Password</label>
                    <input
                      type="password"
                      value={authPassword}
                      onChange={(e) => setAuthPassword(e.target.value)}
                      placeholder="Enter your password"
                      required
                      minLength={6}
                    />
                  </div>

                  {authError && <p className="auth-error">{authError}</p>}
                  {authSuccess && <p className="auth-success">{authSuccess}</p>}

                  <button type="submit" className="btn-auth" disabled={authLoading}>
                    {authLoading ? 'Please wait...' : (authMode === 'login' ? 'Login' : 'Create Account')}
                  </button>
                </form>

                {authMode === 'login' && (
                  <p className="auth-forgot">
                    <button onClick={() => { setShowResetPassword(true); setAuthError(''); setAuthSuccess(''); }}>
                      Forgot Password?
                    </button>
                  </p>
                )}

                <p className="auth-switch">
                  {authMode === 'login' ? (
                    <>Don't have an account? <button onClick={() => setAuthMode('signup')}>Sign Up</button></>
                  ) : (
                    <>Already have an account? <button onClick={() => setAuthMode('login')}>Login</button></>
                  )}
                </p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="landing-hero">
        <div className="landing-hero-content">
          <div className="landing-logo">
            <h1>ClubLink TT</h1>
          </div>
          <p className="landing-tagline">Football Club Management Platform</p>
          <p className="landing-subtitle">
            The complete solution for Trinidad & Tobago football clubs
          </p>
          <div className="landing-cta-buttons">
            <a href="#find-club" className="btn-primary">Find Your Club</a>
            <Link to="/create-club" className="btn-secondary">Register Your Club</Link>
          </div>
        </div>
        <div className="landing-hero-flag">
          <div className="tt-flag">
            <div className="flag-stripe red"></div>
            <div className="flag-stripe black"></div>
            <div className="flag-stripe red"></div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="landing-section pricing-section">
        <h2>Simple, Transparent Pricing</h2>
        <p className="section-subtitle">Everything you need to manage your football club</p>
        
        <div className="pricing-card">
          <div className="pricing-amount">
            <span className="currency">TT$</span>
            <span className="price">500</span>
            <span className="period">/month</span>
          </div>
          <ul className="pricing-features">
            <li>Unlimited players & teams</li>
            <li>Player management & stats</li>
            <li>Schedule & event management</li>
            <li>Online registration system</li>
            <li>Kit shop with payments</li>
            <li>Attendance tracking</li>
            <li>Photo gallery</li>
            <li>Document storage</li>
            <li>Email notifications</li>
            <li>Custom club branding</li>
          </ul>
          <Link to="/create-club" className="btn-primary btn-large">Get Started</Link>
        </div>
      </section>

      {/* Find Your Club Section */}
      <section id="find-club" className="landing-section find-club-section">
        <h2>Find Your Club</h2>
        <p className="section-subtitle">Search for your club or browse all registered clubs</p>
        
        <div className="search-container">
          <input
            type="text"
            placeholder="Search clubs by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="club-search-input"
          />
          <span className="search-icon">🔍</span>
        </div>

        {loading ? (
          <div className="loading-spinner">Loading clubs...</div>
        ) : filteredClubs.length === 0 ? (
          <div className="no-clubs">
            <p>No clubs found{searchTerm && ` matching "${searchTerm}"`}</p>
            <Link to="/create-club" className="btn-primary">Register Your Club</Link>
          </div>
        ) : (
          <div className="clubs-grid">
            {filteredClubs.map((club, index) => (
              <a
                key={club.id}
                href={`/?club=${club.subdomain}`}
                className={`club-card ${index === 0 ? 'featured' : ''}`}
                style={{
                  '--club-primary': club.primaryColor || '#1e3a5f',
                  '--club-secondary': club.secondaryColor || '#ce1126'
                }}
              >
                {index === 0 && <span className="featured-badge">Featured</span>}
                <div className="club-card-logo" style={{ background: club.primaryColor || '#1e3a5f' }}>
                  <span className="club-initial">{club.name.charAt(0)}</span>
                </div>
                <div className="club-card-info">
                  <h3>{club.name}</h3>
                  <p>{club.address || 'Trinidad & Tobago'}</p>
                </div>
                <div className="club-card-colors">
                  <span className="color-dot" style={{ background: club.primaryColor || '#1e3a5f' }}></span>
                  <span className="color-dot" style={{ background: club.secondaryColor || '#ce1126' }}></span>
                </div>
                <span className="visit-link">Visit Site →</span>
              </a>
            ))}
          </div>
        )}
      </section>

      {/* Features Section */}
      <section className="landing-section features-section">
        <h2>Everything Your Club Needs</h2>
        <p className="section-subtitle">Powerful tools to manage your football club</p>
        
        <div className="features-grid">
          {features.map((feature, index) => (
            <div key={index} className="feature-card">
              <span className="feature-icon">{feature.icon}</span>
              <h3>{feature.title}</h3>
              <p>{feature.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="landing-section cta-section">
        <div className="cta-content">
          <h2>Ready to Get Started?</h2>
          <p>Join ClubLink TT and take your club to the next level</p>
          <Link to="/create-club" className="btn-primary btn-large">Register Your Club</Link>
          <p className="cta-price">TT$ 500/month - Cancel anytime</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <span>ClubLink TT</span>
          </div>
          <p className="footer-tagline">Football Club Management Platform</p>
          <div className="footer-flag">
            <div className="tt-flag-small">
              <div className="flag-stripe red"></div>
              <div className="flag-stripe black"></div>
              <div className="flag-stripe red"></div>
            </div>
            <span>Made for Trinidad & Tobago</span>
          </div>
          <p className="footer-copyright">© 2026 ClubLink TT. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default Landing;
